import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

import {
  TOAST_POSITION_BY_USAGE_TYPE,
  UNSUITABLE_REASON_KEYS,
} from "@/components/shared/business/UnsuitableReason/constants/unsuitableReason.constants";
import { UnsuitableReasonUsageType } from "@/components/shared/business/UnsuitableReason/types/unsuitableReason.types";
import { queryClient } from "@api/queryClient";
import { QUERY_KEYS } from "@api/queryKeys";
import { DRAFT_STATUS_CODE } from "@constants/draftStatusCode.constants";
import { ROUTE_PATHS } from "@routes/routePath";
import { articleInfoApis } from "@services/articleInfo.service";
import { getPressIdFromURL } from "@utils/getStateFromURL";
import { toastFail, toastSuccess } from "@utils/toast";

import { adaptArticleInfoData } from "./adaptArticleInfo.data";
import {
  GetArticleInfoResponse,
  ReviewRequiredInformation,
  ReviewRequiredInformationList,
} from "./articleInfo.interface";

import type { PutArticleInfoArg } from "@interface/articleInfo.interface";

export const useArticleInfoQuery = (
  draftArticleId = Number(getPressIdFromURL()),
) => {
  const query = useSuspenseQuery({
    queryKey: [QUERY_KEYS.articleInfo, draftArticleId],
    queryFn: () => articleInfoApis.get(draftArticleId),
    select: ({ data }) => adaptArticleInfoData(data),
    gcTime: 0,
    staleTime: 0,
  });

  return query;
};

export const useArticleInfoMutation = (
  draftArticleId = Number(getPressIdFromURL()),
) => {
  const mutation = useMutation({
    mutationFn: (args: Omit<PutArticleInfoArg, "draftArticleId">) =>
      articleInfoApis.put({
        draftArticleId,
        ...args,
      }),
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: [QUERY_KEYS.articleInfo, draftArticleId],
      });
    },
    onError: () => {
      toastFail("Failed to update article info");
    },
  });

  return mutation;
};

export const useReviewRequiredMutation = (
  unsuitableReasonUsageType: UnsuitableReasonUsageType,
) => {
  const QUERY_KEYS_BY_USAGE_TYPE: Record<
    UnsuitableReasonUsageType,
    [string, number]
  > = {
    [UNSUITABLE_REASON_KEYS.pressList]: [QUERY_KEYS.articleInfo, 0],
    [UNSUITABLE_REASON_KEYS.ctgList]: [QUERY_KEYS.articleInfo, 0],
    [UNSUITABLE_REASON_KEYS.publicationList]: [QUERY_KEYS.articleInfo, 0],
    [UNSUITABLE_REASON_KEYS.pressDetail]: [
      QUERY_KEYS.articleInfo,
      Number(getPressIdFromURL()),
    ],
    [UNSUITABLE_REASON_KEYS.ctgDetail]: [
      QUERY_KEYS.articleInfo,
      Number(getPressIdFromURL()),
    ],
    [UNSUITABLE_REASON_KEYS.regulatoryList]: [
      QUERY_KEYS.articleInfo,
      Number(getPressIdFromURL()),
    ],
    [UNSUITABLE_REASON_KEYS.regulatoryDetail]: [
      QUERY_KEYS.articleInfo,
      Number(getPressIdFromURL()),
    ],
    [UNSUITABLE_REASON_KEYS.publicationDetail]: [
      QUERY_KEYS.articleInfo,
      Number(getPressIdFromURL()),
    ],
  };
  const mutation = useMutation({
    mutationFn: ({
      pressId,
      requiredReviewInfo,
    }: {
      pressId: number;
      requiredReviewInfo: ReviewRequiredInformation;
    }) => {
      const isEnabledReviewRequired: UnsuitableReasonUsageType[] = [
        UNSUITABLE_REASON_KEYS.pressList,
        UNSUITABLE_REASON_KEYS.pressDetail,
      ];
      if (!isEnabledReviewRequired.includes(unsuitableReasonUsageType)) {
        return Promise.resolve();
      }
      if (requiredReviewInfo.reviewId.length === 0) {
        return Promise.resolve();
      }
      return articleInfoApis.reviewRequiredPut(pressId, requiredReviewInfo);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: QUERY_KEYS_BY_USAGE_TYPE[unsuitableReasonUsageType],
      });
    },
    onError: () => {
      toastFail("Failed to update article info");
    },
  });

  return mutation;
};

export const useSetUnsuitableArticleInfoMutation = (
  unsuitableReasonUsageType: UnsuitableReasonUsageType,
) => {
  const QUERY_KEYS_BY_USAGE_TYPE: Record<
    UnsuitableReasonUsageType,
    [string, number]
  > = {
    [UNSUITABLE_REASON_KEYS.pressList]: [QUERY_KEYS.articleInfo, 0],
    [UNSUITABLE_REASON_KEYS.ctgList]: [QUERY_KEYS.articleInfo, 0],
    [UNSUITABLE_REASON_KEYS.publicationList]: [QUERY_KEYS.articleInfo, 0],
    [UNSUITABLE_REASON_KEYS.pressDetail]: [
      QUERY_KEYS.articleInfo,
      Number(getPressIdFromURL()),
    ],
    [UNSUITABLE_REASON_KEYS.ctgDetail]: [
      QUERY_KEYS.articleInfo,
      Number(getPressIdFromURL()),
    ],
    [UNSUITABLE_REASON_KEYS.regulatoryList]: [
      QUERY_KEYS.articleInfo,
      Number(getPressIdFromURL()),
    ],
    [UNSUITABLE_REASON_KEYS.regulatoryDetail]: [
      QUERY_KEYS.articleInfo,
      Number(getPressIdFromURL()),
    ],
    [UNSUITABLE_REASON_KEYS.publicationDetail]: [
      QUERY_KEYS.articleInfo,
      Number(getPressIdFromURL()),
    ],
  };
  const toastPosition = TOAST_POSITION_BY_USAGE_TYPE[unsuitableReasonUsageType];
  const currentQueryKey = QUERY_KEYS_BY_USAGE_TYPE[unsuitableReasonUsageType];

  const mutation = useMutation({
    mutationFn: ({
      draftArticleId,
      articleRejectReason,
      ...rest
    }: {
      draftArticleId: number;
      articleRejectReason: string;
    }) =>
      articleInfoApis.put({
        draftArticleId,
        articleRejectReason,
        draftArticleStatus: DRAFT_STATUS_CODE.unsuitable,
        ...rest,
      }),
    onSuccess: async () => {
      toastSuccess(
        "Successfully updated unsuitable reason",
        "green",
        toastPosition,
      );
      await queryClient.refetchQueries({
        queryKey: currentQueryKey,
      });
    },
    onError: () => {
      toastFail("Failed to update unsuitable reason", toastPosition);
    },
  });

  return mutation;
};

export const usePublishableArticleInfoMutation = () => {
  const location = useLocation();
  const toastPosition =
    location.pathname === ROUTE_PATHS.pressList ? "bottomLeft" : "topCenter";

  const mutation = useMutation({
    mutationFn: ({
      draftArticleId,
      isPublishable,
    }: {
      draftArticleId: number;
      isPublishable: boolean;
    }) =>
      articleInfoApis.put({
        draftArticleId,
        publishable: isPublishable,
      }),
    onSuccess: () => {
      toastSuccess("Successfully updated publishable", "green", toastPosition);
    },
    onError: () => {
      toastFail("Failed to update publishable", toastPosition);
    },
  });

  return mutation;
};

export const getArticleInfoData = (
  draftArticleId = Number(getPressIdFromURL()),
) => {
  const articleInfo = queryClient.getQueryData<GetArticleInfoResponse>([
    QUERY_KEYS.articleInfo,
    draftArticleId,
  ]);

  if (!articleInfo) {
    return null;
  }

  return {
    ...articleInfo,
    data: adaptArticleInfoData(articleInfo.data),
  };
};

export const useNewInfoReviewRequiredMutation = () => {
  const mutation = useMutation({
    mutationFn: ({
      pressId,
      requiredReviewInformationList,
    }: {
      pressId: number;
      requiredReviewInformationList: ReviewRequiredInformationList;
    }) => {
      if (requiredReviewInformationList.length === 0) {
        return Promise.resolve();
      }
      return articleInfoApis.updateNewInfoReviewRequired(
        pressId,
        requiredReviewInformationList,
      );
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: [QUERY_KEYS.articleInfo, Number(getPressIdFromURL())],
      });
    },
    onError: () => {
      toastFail("Failed to update article info");
    },
  });

  return mutation;
};
